import { request } from '@/utils/http'
import type { BookingPageVO } from '@api/order/types'
import type { InquiryBaseModel } from '@api/inquiry/inquiry-base'

/**
 * 询价AI识别响应结果
 */
export interface AiRecognitionResponse {
  // 基本状态和配置
  status?: number
  inquiryNo?: string
  trunkLineEnable?: boolean
  prePortEnable?: boolean
  lastMileEnable?: boolean
  inquirerId?: number
  source?: number
  palletNature?: number

  // 货好时间相关
  cargoReadyTimeType?: string
  cargoReadyTimeRange?: number
  cargoReadyTimeDate?: string

  // 货盘质量和服务条款
  palletQuality?: number
  serviceTerm?: string
  customServiceTerm?: string

  // 委托单位信息
  clientType?: number
  clientId?: number
  clientTitle?: string

  // 装箱门点地址信息
  stuffingCountryId?: number
  stuffingProvinceId?: number
  stuffingCityId?: number
  stuffingDistrictId?: number
  stuffingStreetId?: number
  stuffingVillageId?: number
  stuffingDetailAddress?: string

  // 配送地址信息
  deliveryAddressType?: number
  postalCode?: string
  deliveryCountryId?: number
  deliveryProvinceId?: number
  deliveryCityId?: number
  deliveryDistrictId?: number
  deliveryAddress?: string
  warehouseId?: number

  // 运输路线信息
  directTransit?: number
  routeId?: number
  commerceId?: number
  loadingPortId?: number
  dischargingPortId?: number
  destinationPortId?: number
  transferPortId1?: number
  transferPortId2?: number
  transferPortId3?: number

  // 货物信息
  cargoType?: number
  hazardClass?: number
  unNo?: number
  temperature?: number
  ventilationQuantity?: number
  cargoDescription?: string
  weight?: number
  cubage?: number
  length?: number
  width?: number
  height?: number

  // 时间信息
  etd?: string
  eta?: string

  // 承运人和其他信息
  carrierId?: string
  remarks?: string
  seaEq?: string

  // 价格ID相关
  trunkLinePriceId?: number
  prePortPriceId?: number
  lastMilePriceId?: number

  // 报价状态相关
  quotationStatus?: number
  trunkLineQuotationStatus?: number
  prePortQuotationStatus?: number
  lastMileQuotationStatus?: number
  prePortRejectReason?: string
  trunkLineRejectReason?: string
  lastMileRejectReason?: string
}

/**
 * 国内发货地址识别响应结果
 */
export interface PreportAddressResponse {
  stuffingCountryId?: string
  stuffingProvinceId?: string
  stuffingCityId?: string
  stuffingDistrictId?: string
  stuffingStreetId?: string
  stuffingVillageId?: string
  stuffingDetailAddress?: string
}

/**
 * 尾程送货地址识别响应结果
 */
export interface LastmileAddressResponse {
  deliveryAddressType?: number | null
  postalCode?: string
  deliveryCountryId?: string
  deliveryProvinceId?: string
  deliveryCityId?: string
  deliveryDistrictId?: string
  deliveryAddress?: string
  warehouseId?: string
}

/**
 * 营业执照AI识别响应结果
 */
export interface BusinessLicenseResponse {
  /** 公司名称 */
  companyName?: string
  /** 营业执照号 */
  licenseNumber?: string
  /** 地址 */
  address?: string
  /** 文件code */
  file?: string
}

/**
 * 地址提取返回结果
 */
export interface AddressExtractResponse {
  country?:string
  province?:string
  city?:string
  district?:string
  street?:string
  village?:string
  detailAddress?:string
  // 仓库识别相关字段
  deliveryAddressType?: string  // 后端返回的是字符串类型
  warehouseName?: string
}

/**
 * AI 操作对应的类型
 */
export enum AiOperationType {
  INQUIRY_EXTRACT = 1,
  PREPORT_ADDRESS_EXTRACT = 2,
  LASTMILE_ADDRESS_EXTRACT = 3,
  // 营业执照识别
  BUSINESS_LICENSE = 4,
  // 整箱运价识别
  FCL_PRICE_RECOGNITION = 5,
  // 港前运价识别
  PRE_PORT_PRICE_RECOGNITION = 6,

  // 尾程运价识别
  LAST_MILE_PRICE_RECOGNITION = 7,
  // 地址提取
  ADDRESS_EXTRACT = 8
}

/**
 * 运价类型枚举
 */
export enum PriceTypeEnum {
  FCL = 1,           // 整箱
  PRE_PORT = 2,      // 港前
  LAST_MILE = 3,     // 尾程
  LCL = 4,           // 拼箱
  AIR = 5            // 空运
}

/**
 * 任务操作类型枚举
 */
export enum TaskOperationTypeEnum {
  IMPORT = 1,        // 导入
  EXPORT = 2         // 导出
}

/**
 * AI运价解析请求参数
 */
export interface AiDocumentParseRequest {
  priceType: PriceTypeEnum | string
  operationType: TaskOperationTypeEnum | string
  files: File[]
}

/**
 * AI运价解析响应结果
 */
export type AiDocumentParseResponse = string[]

/**
 * ai识别
 * @param operationType
 * @param params
 */
export const extractInquiryInfo= <T>(operationType:AiOperationType,params: any) => {
  return request.post<T>('/universal/api/ai/recognition', {
    operationType,
    params
  })
}

/**
 * AI运价解析接口
 * @param data - 请求参数
 * @returns Promise<string[]> 解析结果列表
 */
export const uploadDocumentParse = (data: AiDocumentParseRequest): Promise<AiDocumentParseResponse> => {
  const formData = new FormData()

  // 添加参数 - 发送数字值
  formData.append('priceType', data.priceType.toString())
  formData.append('operationType', data.operationType.toString())

  // 添加文件
  data.files.forEach(file => {
    formData.append('files', file)
  })

  return request.post<AiDocumentParseResponse>('/universal/api/ai/document-parse', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * AI识别文件处理接口
 * @param file 要识别的文件
 * @param type 操作类型 (AiOperationType)
 * @returns Promise<T> 识别结果
 */
export const aiRecognitionFile = <T>(file: File, type: AiOperationType): Promise<T> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('type', type.toString())

  return request.post<T>('/universal/api/ai/recognition/file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}


/**
 * AI托书识别
 * @param file 要识别的文件
 * @returns Promise<T> 识别结果
 */
export const bookingDocumentParse = (file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  return request.post<BookingPageVO>('/booking/task/booking-document-parse', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 询价AI文本识别
 */
export const inquiryAiRecognition = (text: string) => {
  return request.post<InquiryBaseModel>('/vas/ai/inquiryRecognition', { text })
}

/**
 * 询价AI文件识别
 */
export const inquiryFileAiRecognition = (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return request.post<InquiryBaseModel>('/vas/ai/inquiryFileRecognition', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}
